import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import TelegramIcon from "@mui/icons-material/Telegram";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import PublicIcon from "@mui/icons-material/Public";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import StarIcon from "@mui/icons-material/Star";
import { TELEGRAM_GROUPS, TelegramGroup } from "utils/telegramGroups";

const kindLabel: Record<TelegramGroup["kind"], string> = {
  main: "Hauptgruppe",
  offtop: "Off-Topic",
  city: "Stadt",
  state: "Bundesland",
};

const kindIcon: Record<TelegramGroup["kind"], React.ReactNode> = {
  main: <StarIcon fontSize="small" />,
  offtop: <ChatBubbleOutlineIcon fontSize="small" />,
  city: <LocationCityIcon fontSize="small" />,
  state: <PublicIcon fontSize="small" />,
};

function CommunitiesGrid() {
  return (
    <Box
      component="section"
      id="communities"
      sx={{ py: { xs: 8, md: 12 }, scrollMarginTop: 80 }}
    >
      <Container maxWidth="lg">
        <Stack spacing={1.5} sx={{ mb: { xs: 5, md: 7 } }}>
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: 30, md: 44 },
              fontWeight: 900,
              lineHeight: 1.15,
              color: "#2c2936",
            }}
          >
            Finde deine{" "}
            <Box
              component="span"
              sx={{
                background:
                  "linear-gradient(131.35deg, #cb97ff 7.53%, #583dff 94.2%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Gruppe
            </Box>
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 15, md: 17 },
              color: "#4a4658",
              maxWidth: 700,
              lineHeight: 1.6,
            }}
          >
            Für die größeren Städte gibt es eigene Telegram-Gruppen. Für kleinere
            Städte findet ihr euch in den Bundesland-Chats über Topics zusammen.
          </Typography>
        </Stack>

        <Grid container spacing={{ xs: 2, md: 3 }}>
          {TELEGRAM_GROUPS.map((g) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={g.username}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor:
                    g.kind === "main" ? "#583dff" : "rgba(0,0,0,0.06)",
                  background:
                    g.kind === "main"
                      ? "linear-gradient(180deg, rgba(88,61,255,0.10) 0%, rgba(255,255,255,1) 100%)"
                      : "#fff",
                  transition:
                    "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 18px 40px -20px rgba(88,61,255,0.35)",
                    borderColor: "#583dff",
                  },
                }}
              >
                <CardActionArea
                  href={g.url}
                  target="_blank"
                  rel="noopener"
                  sx={{ height: "100%", p: 0 }}
                >
                  <CardContent
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      p: { xs: 2.5, md: 3 },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip
                        size="small"
                        icon={kindIcon[g.kind] as React.ReactElement}
                        label={kindLabel[g.kind]}
                        sx={{
                          fontWeight: 600,
                          background:
                            g.kind === "main"
                              ? "linear-gradient(131.35deg, #cb97ff 7.53%, #583dff 94.2%)"
                              : "rgba(88,61,255,0.08)",
                          color: g.kind === "main" ? "#fff" : "#583dff",
                          "& .MuiChip-icon": {
                            color: g.kind === "main" ? "#fff" : "#583dff",
                          },
                        }}
                      />
                    </Stack>
                    <Typography
                      component="h3"
                      lang="de"
                      sx={{
                        fontSize: { xs: 19, md: 21 },
                        fontWeight: 800,
                        color: "#2c2936",
                        lineHeight: 1.2,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                        hyphens: "auto",
                      }}
                    >
                      {g.city}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: "#5d5870",
                        lineHeight: 1.55,
                        flexGrow: 1,
                      }}
                    >
                      {g.description}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        pt: 1,
                        color: "#583dff",
                        fontWeight: 600,
                      }}
                    >
                      <TelegramIcon fontSize="small" />
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          fontFamily:
                            "Menlo, Monaco, Consolas, 'Courier New', monospace",
                          wordBreak: "break-all",
                          minWidth: 0,
                        }}
                      >
                        {g.username}
                      </Typography>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

export default CommunitiesGrid;
